import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Navigation, Star, Plus, Check, Phone, Globe, Film, ArrowRight } from 'lucide-react';
import GymReviewPanel from '../../components/GymReviewPanel';
import { safeId } from './data';

export const GymCard = ({ gym, isSelected, onToggleCompare, compareCount, distance, reviewStats, content }) => {
  const isOfficial = gym.tier.toLowerCase().includes('official') || gym.tier.toLowerCase().includes('performance center');
  const canSelect = isSelected || compareCount < 3;
  const [showReviews, setShowReviews] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const gymKey = `${gym.name}|${gym.city}|${gym.state}`;
  const stats = reviewStats?.[gymKey];
  const hasContent = content && content.videos && content.videos.length > 0;
  const id = safeId(gym.name);

  return (
    <div
      className={`bg-zinc-900/90 border p-5 transition-all hover:translate-y-[-1px] relative ${
        isSelected
          ? 'border-[#e8ff00] ring-1 ring-[#e8ff00]/20'
          : gym.priority === 'HIGH' ? 'border-[#e8ff00]/30 hover:border-[#e8ff00]/60' : 'border-zinc-800 hover:border-zinc-600'
      }`}
      data-testid={`gym-card-${id}`}
    >
      <button
        onClick={() => canSelect && onToggleCompare(gym)}
        className={`absolute top-3 right-3 w-7 h-7 flex items-center justify-center border transition-all ${
          isSelected
            ? 'bg-[#e8ff00] border-[#e8ff00] text-black'
            : canSelect
              ? 'bg-zinc-800 border-zinc-700 text-zinc-500 hover:border-[#e8ff00]/50 hover:text-[#e8ff00]'
              : 'bg-zinc-800/50 border-zinc-800 text-zinc-700 cursor-not-allowed'
        }`}
        title={isSelected ? 'Remove from compare' : compareCount >= 3 ? 'Max 3 gyms' : 'Add to compare'}
        data-testid={`gym-compare-toggle-${id}`}
      >
        {isSelected ? <Check className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
      </button>

      <div className="flex items-start justify-between gap-3 mb-3 pr-8">
        <div className="flex-1 min-w-0">
          <h4 className="text-white font-heading text-base leading-tight">{gym.name}</h4>
          <p className="text-zinc-500 text-xs mt-1 flex items-center gap-1.5">
            <MapPin className="w-3 h-3 flex-shrink-0" /> {gym.city}, {gym.state} {gym.zip ? `— ${gym.zip}` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {distance !== null && distance !== undefined && (
            <div className="flex items-center gap-1 px-2 py-1 bg-[#e8ff00]/10 border border-[#e8ff00]/30" data-testid={`gym-distance-${id}`}>
              <Navigation className="w-3 h-3 text-[#e8ff00]" />
              <span className="text-[#e8ff00] text-xs font-heading">{distance} mi</span>
            </div>
          )}
          <div className="flex items-center gap-1 px-2 py-1 bg-zinc-800 border border-zinc-700">
            <Star className="w-3 h-3 text-[#e8ff00]" />
            <span className="text-white text-xs font-heading">{stats ? stats.avg : gym.rating}</span>
            {stats && <span className="text-zinc-500 text-[10px]">({stats.count})</span>}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-3">
        <span className={`inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-wider font-heading ${
          isOfficial ? 'bg-[#e8ff00]/10 text-[#e8ff00] border border-[#e8ff00]/30' : 'bg-zinc-800 text-zinc-400 border border-zinc-700'
        }`}>
          {isOfficial ? 'Official' : 'Aligned'}
        </span>
        {gym.raceSims && (
          <span className="inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-wider font-heading bg-[#E63946]/10 text-[#E63946] border border-[#E63946]/30">
            Race Sims
          </span>
        )}
        {gym.hyroxClasses && (
          <span className="inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-wider font-heading bg-zinc-800 text-zinc-400 border border-zinc-700">
            HYROX Classes
          </span>
        )}
        {hasContent && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider font-heading bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/30" data-testid={`gym-filmed-badge-${id}`}>
            <Film className="w-3 h-3" /> Filmed
          </span>
        )}
      </div>

      <p className="text-zinc-500 text-[11px] mb-2">{gym.tier}</p>

      {gym.notableInfo && (
        <p className="text-zinc-400 text-xs leading-relaxed mb-3">{gym.notableInfo}</p>
      )}

      {gym.pricing && gym.pricing !== 'Inquire' && (
        <p className="text-zinc-500 text-xs mb-3">
          <span className="text-[#e8ff00] font-heading">Pricing:</span> {gym.pricing}
        </p>
      )}

      <div className="flex gap-2 pt-3 border-t border-zinc-800/50">
        {gym.website && (
          <a
            href={gym.website}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-[#e8ff00] text-black text-xs font-heading uppercase tracking-wider hover:opacity-90 transition-opacity"
            data-testid={`gym-visit-${id}`}
          >
            Visit <Globe className="w-3 h-3" />
          </a>
        )}
        {gym.phone && (
          <a
            href={`tel:${gym.phone}`}
            className="flex items-center justify-center gap-1.5 px-3 py-2 border border-zinc-700 text-zinc-400 text-xs font-heading uppercase tracking-wider hover:border-[#e8ff00]/40 hover:text-[#e8ff00] transition-colors"
          >
            <Phone className="w-3 h-3" /> Call
          </a>
        )}
        <button
          onClick={() => setShowReviews((v) => !v)}
          className={`flex items-center justify-center gap-1.5 px-3 py-2 border text-xs font-heading uppercase tracking-wider transition-colors ${
            showReviews
              ? 'border-[#e8ff00]/40 text-[#e8ff00] bg-[#e8ff00]/5'
              : 'border-zinc-700 text-zinc-400 hover:border-[#e8ff00]/40 hover:text-[#e8ff00]'
          }`}
          data-testid={`gym-reviews-toggle-${id}`}
        >
          <Star className="w-3 h-3" /> {stats ? stats.count : 0}
        </button>
        {hasContent && (
          <button
            onClick={() => setShowContent((v) => !v)}
            className={`flex items-center justify-center gap-1.5 px-3 py-2 border text-xs font-heading uppercase tracking-wider transition-colors ${
              showContent
                ? 'border-[#00F0FF]/40 text-[#00F0FF] bg-[#00F0FF]/5'
                : 'border-zinc-700 text-zinc-400 hover:border-[#00F0FF]/40 hover:text-[#00F0FF]'
            }`}
            data-testid={`gym-content-toggle-${id}`}
          >
            <Film className="w-3 h-3" /> Content
          </button>
        )}
      </div>

      {hasContent && showContent && (
        <div className="mt-4 pt-4 border-t border-[#00F0FF]/20" data-testid={`gym-content-panel-${id}`}>
          <p className="text-[#00F0FF] text-[10px] uppercase tracking-wider font-heading mb-2 flex items-center gap-1.5">
            <Film className="w-3 h-3" /> Content Portfolio
          </p>
          {content.description && (
            <p className="text-zinc-400 text-xs mb-3">{content.description}</p>
          )}
          <div className="grid grid-cols-2 gap-2 mb-3">
            {content.videos.map((vid, vi) => (
              <div key={vid.url || vi} className="relative aspect-video bg-zinc-800 border border-zinc-700 overflow-hidden group cursor-pointer" data-testid={`gym-video-${vi}`}>
                <video
                  src={vid.url}
                  poster={vid.poster}
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  className="w-full h-full object-cover"
                  onMouseEnter={(e) => e.target.play()}
                  onMouseLeave={(e) => { e.target.pause(); e.target.currentTime = 0; }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent pointer-events-none">
                  <div className="absolute bottom-0 left-0 right-0 p-2">
                    <span className="text-[#00F0FF] text-[8px] uppercase tracking-wider">{vid.type}</span>
                    <p className="text-white text-[10px] font-heading">{vid.title}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <Link to="/hyrox-content" className="inline-flex items-center gap-1.5 text-[#00F0FF] text-[10px] uppercase tracking-wider font-heading hover:underline" data-testid="gym-content-cta">
            Want this for your gym? <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      )}

      <GymReviewPanel gym={gym} isOpen={showReviews} onClose={() => setShowReviews(false)} />
    </div>
  );
};
